/* eslint-disable no-script-url */
/* eslint-disable jsx-a11y/anchor-is-valid */

import React from "react";

/**
 * Used in results-summary for computing last item in the page.
 * startIndex, resultsPerPage are known and we need to compute based
 * on total items in result
 * @param startIndex
 * @param resultsPerPage
 * @param resultsCount
 */
export const computeIndexOfLastItemInPage = (
  startIndex: number,
  resultsPerPage: number,
  resultsCount: number
): number | undefined => {
  /*
   * There are 4 scenarios that could occur that changes the logic and numbers
   * we display in the summary.
   */
  const resultCountMultipleOfPageSize = resultsCount % resultsPerPage === 0;
  const lastPageOfResults = startIndex + resultsPerPage > resultsCount;
  const pageNotStartOrLast = startIndex + resultsPerPage < resultsCount;
  const resultsLessThanPageSize = startIndex + resultsCount < resultsPerPage;
  if (resultCountMultipleOfPageSize) {
    return startIndex + resultsPerPage;
  }
  if (lastPageOfResults) {
    return resultsCount - startIndex + startIndex;
  }
  if (pageNotStartOrLast) {
    return startIndex + resultsPerPage;
  }
  if (resultsLessThanPageSize) {
    return resultsCount;
  }
  return undefined;
};

/**
 * Used in results-per-page.tsx
 * This method is used to compute the dropdown list options based on resultCount,
 * resultsPerPage & dropDown prop contents
 */
export const computeDropdownListHelper = (
  resultsPerPage: number,
  resultsCount: number,
  resultsPerPageOptions: number[]
): number[] => {
  const oneResultFound = resultsCount === 1;
  const resultsPerPageOptionsSorted = [...resultsPerPageOptions].sort(
    (a, b) => a - b
  );

  /*
   *   This happens when props have resultCount of 5 and resultsPerPage is 10,
   *   we need to show [5] or resultCount of 12 and resultsPerPage is 15, we need to show [12]
   */
  const resultsCountIsLessThanPageSize =
    resultsCount < resultsPerPage &&
    resultsCount <= resultsPerPageOptionsSorted[0];

  /*
   * When we have one result item or results are less than the smallest page size in the dropdown,
   * then we change the lease value in the dropdown option
   */
  if (oneResultFound || resultsCountIsLessThanPageSize) {
    console.info(
      "ResultsCount is smaller than resultsPerPage, so only one option will be shown in pagination dropdown"
    );
    return [resultsPerPageOptionsSorted[0]];
  }

  /*
   * The dropdown building logic is as follows
   * - pick the options which are smaller than the results count
   * - if some option is bigger than the results count, then its not shown
   * - if result count is between 2 options, then we the immediate next option
   */
  const newDropdownOptions = resultsPerPageOptions.filter(
    (listItem) => listItem <= resultsCount
  );

  const newOptionsHasPartialItems =
    newDropdownOptions.length < resultsPerPageOptions.length;
  const resultsCountGreaterThanLastOptionValue =
    resultsCount > newDropdownOptions[newDropdownOptions.length - 1];

  /*
   *  This happens when options are [10, 25, 50] and resultCount is 40. We need to display [10, 25, 50]
   */
  if (newOptionsHasPartialItems && resultsCountGreaterThanLastOptionValue) {
    return newDropdownOptions.concat(
      resultsPerPageOptionsSorted[newDropdownOptions.length]
    );
  }

  return newDropdownOptions;
};

/**
 * Compute the start pageNumber for the Pagination view
 * Example 1: pageNum = 13, numberOfPagesToDisplay = 5, StartIndex to be 10
 * Example 2: pageNum = 3, numberOfPagesToDisplay = 5, StartIndex to be 1
 * Example 3: pageNum = 23, numberOfPagesToDisplay = 10, StartIndex to be 20
 * Example 4: pageNum = 5, numberOfPagesToDisplay = 5, StartIndex to be 1
 * Example 5: pageNum = 15, numberOfPagesToDisplay = 5, StartIndex to be 11
 */
export const computeFirstPageNumberToDisplay = (
  numberOfPagesToDisplay: number
): Function => {
  return (pageNumber: number): number => {
    let startIndex;
    if (pageNumber <= numberOfPagesToDisplay) {
      // Example 2
      startIndex = 0;
    } else if (pageNumber % numberOfPagesToDisplay === 0) {
      // Example 4 & 5
      startIndex =
        Math.floor((pageNumber - 1) / numberOfPagesToDisplay) *
        numberOfPagesToDisplay;
    } else {
      // Example 1 & 3
      startIndex =
        Math.floor(pageNumber / numberOfPagesToDisplay) *
        numberOfPagesToDisplay;
    }

    return startIndex + 1;
  };
};

interface disabledLink extends React.HTMLProps<HTMLAnchorElement> {
  ariaLabel: string;
  label: string;
  type: "next" | "previous";
}

/**
 * Pure function to generate disable prev/next link
 */
export const DisabledLink = ({
  label,
  type,
}: disabledLink): React.ReactElement => (
  <span
    className={`spark-pagination__arrow spark-pagination__arrow--${type} disabled`}
    aria-hidden={true}
  >
    <span className="spark-pagination__arrow-text">{label}</span>
  </span>
);

interface enabledLink extends disabledLink {
  onClick: React.MouseEventHandler<HTMLAnchorElement>;
}

/**
 * Pure function to generate enabled prev/next links
 */
export const EnabledLink = ({
  ariaLabel,
  label,
  type,
  onClick,
}: enabledLink): React.ReactElement => (
  <a
    className={`spark-pagination__arrow spark-pagination__arrow--${type}`}
    aria-label={ariaLabel}
    onClick={onClick}
    href={"#"}
  >
    <span className="spark-pagination__arrow-text">{label}</span>
  </a>
);

/**
 * Pure function to render Disabled Previous Link
 */
export const renderDisabledPrevLink = (
  previousAriaLabel: string,
  previousLabel: string
): React.ReactElement => (
  <DisabledLink
    ariaLabel={previousAriaLabel}
    label={previousLabel}
    type={"previous"}
  />
);

/**
 * Pure function to render enabled previous link
 */
export const renderEnabledPrevLink = (
  previousAriaLabel: string,
  previousLabel: string,
  prevBtnHandler: React.MouseEventHandler<HTMLAnchorElement>
): React.ReactElement => (
  <EnabledLink
    ariaLabel={previousAriaLabel}
    label={previousLabel}
    type={"previous"}
    onClick={prevBtnHandler}
  />
);

/**
 * Pure function to render enabled next link
 */
export const renderEnabledNextLink = (
  nextAriaLabel: string,
  nextLabel: string,
  nextBtnHandler: React.MouseEventHandler<HTMLAnchorElement>
): React.ReactElement => (
  <EnabledLink
    ariaLabel={nextAriaLabel}
    label={nextLabel}
    type={"next"}
    onClick={nextBtnHandler}
  />
);

/**
 * Pure function to render disabled next link
 */
export const renderDisabledNextLink = (
  nextAriaLabel: string,
  nextLabel: string
): React.ReactElement => (
  <DisabledLink ariaLabel={nextAriaLabel} label={nextLabel} type={"next"} />
);
